import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import RequestFilters from "@/domains/evidence-requests/components/RequestFilters";
import { useLazyGetDataBlockRequestsQuery } from "@/redux/service";
import ListItemsCard from "@/components_v2/Card/ListItemsCard";
import BaseTabs from "@/components_v2/Tabs/BaseTabs";
import RequestsAccordion from "@/features/evidence-requests/list/components/RequestsAccordion";
import BaseButton from "@/components_v2/Button/BaseButton";
import CreateDataBlock from "@/domains/datablocks/components/CreateDatablock";
import { RequestStatus } from "@/domains/evidence-requests/constants";
import RequestFormModal from "@/features/evidence-requests/list/components/RequestFormModal";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import EmptyIcon from "@/assets/icons/empty.svg?react";
import DataBlockInviteIcon from "@/assets/icons/datablock-invites.svg?react";
import BaseTag from "@/components_v2/Tags/BaseTag";
import CalendarIcon from "@/assets/icons/calendar.svg?react";
import SurveyIcon from "@/assets/icons/survey-icon.svg?react";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import { BUTTON_SIZES } from "@/components_v2/Button/constants";
import { DataBlockRequest } from "@/redux/datablocks/types";
import { CAN_CREATE_REQUESTS } from "@/features/evidence-requests/list/constants";
import { DataBlockRequestFiltersState } from "@/domains/evidence-requests/types";
import { DataBlockDefaultValues } from "@/domains/datablocks/types";

import "./styles.css";

const DataBlockRequests = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [getDataBlockRequests, { data, isSuccess, isFetching }] =
    useLazyGetDataBlockRequestsQuery();
  const currentUser = useSelector(selectCurrentUser);

  const [filters, setFilters] = useState<DataBlockRequestFiltersState>({
    pagination: "off" as const,
    status: [RequestStatus.PENDING.value, RequestStatus.REVISION_NEEDED.value],
    user: undefined,
  });

  const [createDataBlock, setDataBlockCreate] = useState<{
    open: boolean;
    defaultValues?: DataBlockDefaultValues;
  }>({
    open: false,
    defaultValues: undefined,
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setFilters((prevValue) => ({ ...prevValue, user: currentUser.id }));
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    getDataBlockRequests(filters);
  }, [currentUser, filters]);

  const requestCards = useMemo(() => {
    if (isFetching) {
      return (
        <div className="flex justify-center full-width">
          <BaseSpinner />
        </div>
      );
    }

    if (isSuccess) {
      const results = data as DataBlockRequest[];
      return results.map((request) => {
        const daysLeft = DateTime.fromISO(request.expiresAt).diffNow(
          "days",
        ).days;

        const helpTagContent = () => {
          const days = Math.ceil(daysLeft).toString();

          if (days === "0") return t("datablocks.labels.today");

          return `${days} ${t("datablocks.labels.daysLeft")}`;
        };

        return (
          <ListItemsCard
            key={request.id}
            headerContent={
              <div className="flex items-center self-stretch justify-between">
                <BaseTag label={request.dataTemplate.title} />

                {request.status === RequestStatus.PENDING.value && (
                  <BaseTag
                    label={helpTagContent()}
                    className="list-items-card-help"
                  />
                )}
              </div>
            }
            title={t(
              `datablocks.requestStatus.${request.status.toLowerCase()}`,
            )}
            description={`From ${request.createdBy.fullName}`}
            items={[
              {
                icon: <BaseSvgRenderer component={SurveyIcon} />,
                content: request.message,
              },
              {
                icon: <BaseSvgRenderer component={CalendarIcon} />,
                content: DateTime.fromISO(request.expiresAt).toFormat(
                  "dd MMM yyyy",
                ),
              },
            ]}
            buttonText={
              request.status === RequestStatus.PENDING.value
                ? t("datablocks.labels.createDataBlock")
                : t("datablocks.labels.updateDataBlock")
            }
            onButtonClick={() => {
              if (request.dataBlock) {
                navigate(`/evidence/${request.dataBlock}/edit`);
              } else {
                setDataBlockCreate({
                  open: true,
                  defaultValues: {
                    request: request,
                    dataTemplate: request.dataTemplate.id,
                  },
                });
              }
            }}
          />
        );
      });
    }

    return [];
  }, [isSuccess, isFetching, data]);

  const RequestsReceivedContainer = useMemo(() => {
    return (
      <div className="page-layout">
        <RequestFilters
          filterNames={["type", "search"]}
          setFilters={(filters) =>
            setFilters((prevValue) => ({ ...prevValue, ...filters }))
          }
          className="filter-layout"
        />
        {Array.isArray(requestCards) && requestCards.length ? (
          <div className="data-block-requests-cards full-width gap-400">
            {requestCards}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-100 full-width">
            <BaseSvgRenderer component={EmptyIcon} />
            <h5 className="data-block-requests-empty-message content-center body-m-regular">
              {t("datablocks.labels.noRequestsReceived")}
            </h5>
          </div>
        )}
      </div>
    );
  }, [createDataBlock, setDataBlockCreate, requestCards]);

  return (
    currentUser && (
      <div className="page-layout data-block-requests">
        <div className="page-header">
          <h1 className="header-l-medium">{t("datablocks.labels.requests")}</h1>

          {currentUser.organizationRole &&
            CAN_CREATE_REQUESTS.includes(currentUser.organizationRole) && (
              <BaseButton
                size={BUTTON_SIZES.SMALL}
                leadingIcon={
                  <BaseSvgRenderer
                    width="20"
                    height="20"
                    stroke="white"
                    component={DataBlockInviteIcon}
                  />
                }
                onClick={() => setOpen(true)}
              >
                {t("datablocks.labels.sendRequests")}
              </BaseButton>
            )}
        </div>
        <p className="body-m-regular">
          {t("datablocks.labels.requestsDescription")}
        </p>

        {currentUser.organizationRole &&
        CAN_CREATE_REQUESTS.includes(currentUser.organizationRole) ? (
          <BaseTabs
            items={[
              {
                key: "received",
                label: t("datablocks.labels.requestsReceived"),
                children: RequestsReceivedContainer,
              },
              {
                key: "sent",
                label: t("datablocks.labels.requestsSent"),
                children: <RequestsAccordion />,
              },
            ]}
          />
        ) : (
          RequestsReceivedContainer
        )}

        <CreateDataBlock
          defaultValues={createDataBlock.defaultValues}
          open={createDataBlock.open}
          setOpen={(open) =>
            setDataBlockCreate({ open, defaultValues: undefined })
          }
        />
        <RequestFormModal open={open} setOpen={setOpen} />
      </div>
    )
  );
};

export default DataBlockRequests;
